import React from "react";

import { getArticleTitleAsString } from "@/mk2/apps/blogs/containers/Article/Article.helpers";
import { articleUrl as blogsArticleUrl } from "@/mk2/apps/blogs/utils";
import { StrollerEntity } from "@/mk2/apps/strollers/schemas";
import ReviewArticlePreview, {
    ReviewArticlePreviewPlaceholder,
    ThumbnailSize,
} from "@/mk2/apps/wiki/components/ReviewArticlePreview";
import ReviewBlogPostPreview, {
    ReviewBlogPostPreviewPlaceholder,
} from "@/mk2/apps/wiki/components/ReviewBlogPostPreview";
import ReviewStrollerPreview, {
    ReviewStrollerPreviewPlaceholder,
} from "@/mk2/apps/wiki/components/ReviewStrollerPreview";
import { getArticleBadges } from "@/mk2/components/Badge";
import { getAllDescendantsOfAType } from "@/mk2/helpers/article_ast/utils";
import { cacheLast } from "@/mk2/helpers/cache";
import {
    AST,
    ASTLinkedObjectType,
    BlogPostEntity,
    BlogPostSchema,
    WikiArticleEntity,
} from "@/mk2/schemas";

import styles from "./VariousPreviewsWidget.mscss";

interface TypeIdPair {
    type: string;
    schemaKey?: string;
    id: number;
}
interface OwnProps {
    contentAST: AST;
    wikiArticles: WikiArticleEntity[];
    strollers: StrollerEntity[];
    blogPosts: BlogPostEntity[];
    title?: string;
}

type Props = OwnProps;

const getArticleAndStrollerIdsFromAstCache = cacheLast(true);

class VariousPreviewsWidget extends React.PureComponent<Props> {
    public render() {
        const { contentAST, wikiArticles, strollers, blogPosts, title } = this.props;

        const allIds = getArticleAndStrollerIdsFromAstCache(() => {
            const listOfAllIds: TypeIdPair[] = [];
            const linkToObjectNodes = getAllDescendantsOfAType(contentAST, "link_to_object");
            for (const linkToObjectNode of linkToObjectNodes) {
                listOfAllIds.push({
                    type: linkToObjectNode.obj_type,
                    schemaKey: linkToObjectNode.obj_schema_key,
                    id: linkToObjectNode.obj_id,
                });
            }
            return listOfAllIds;
        }, contentAST);

        const previews = (
            <>
                {allIds.map((typeIdPair, index) => {
                    if (typeIdPair.type === ASTLinkedObjectType.WIKI_ARTICLE) {
                        if (!wikiArticles) return null;
                        const reviewArticle = wikiArticles.find((ra) => ra.id === typeIdPair.id);
                        return reviewArticle ? (
                            <ReviewArticlePreview
                                article={reviewArticle}
                                key={index}
                                thumbnailSize={ThumbnailSize.SMALL}
                                showPerex
                            />
                        ) : (
                            <ReviewArticlePreviewPlaceholder key={index} />
                        );
                    } else if (typeIdPair.type === ASTLinkedObjectType.STROLLERS_STROLLER_DETAIL) {
                        if (!strollers) return null;
                        const stroller = strollers.find((s) => s.id === typeIdPair.id);
                        return stroller ? (
                            <ReviewStrollerPreview key={index} stroller={stroller} />
                        ) : (
                            <ReviewStrollerPreviewPlaceholder key={index} />
                        );
                    } else if (typeIdPair.schemaKey === BlogPostSchema.key) {
                        const blogPost = blogPosts.find((s) => s.id === typeIdPair.id);
                        return blogPost ? (
                            <ReviewBlogPostPreview
                                key={index}
                                className={styles.VariousPreviewsWidget__blogPostPreview}
                                photo={blogPost.photos?.length && blogPost.photos[0]}
                                title={getArticleTitleAsString(blogPost.titleAST)}
                                postUrl={blogsArticleUrl(blogPost)}
                                urlDataStoreClick={"suggested_article_clicked"}
                                badges={getArticleBadges(blogPost.hashtagItems).renderedBadges}
                                commentsCount={blogPost.commentsCount}
                                likersCount={blogPost.likeable.likesCount}
                            />
                        ) : (
                            <ReviewBlogPostPreviewPlaceholder
                                key={index}
                                className={styles.VariousPreviewsWidget__blogPostPreview}
                                author={false}
                                badges
                            />
                        );
                    }
                    return null;
                })}
            </>
        );

        if (title) {
            return (
                <div className={styles.VariousPreviewsWidget__suggestion}>
                    <div className={styles.VariousPreviewsWidget__suggestion__header}>{title}</div>
                    {previews}
                </div>
            );
        } else {
            return previews;
        }
    }
}

export default VariousPreviewsWidget;
